import { useComplianceCalendarEvents } from '@commenda/api';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { adaptEvents, groupEventsByDate } from '@commenda/ui';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useConfig } from 'context/useConfig';
import { useMemo } from 'react';

const MAX_FILINGS = 4;
const WINDOW_DAYS = 7;

/**
 * A quarter's horizon instead of a week's, and a cap that bounds the card's height rather than counting anything.
 *
 * Transfer-pricing deadlines cluster in the weeks around a filing date rather than falling due continuously, so a
 * seven-day lens shows an empty card for the whole run-up to them. Both figures are lens dimensions: neither reads
 * a due date, so nothing about any client's calendar is restated here.
 */
const AM_TP_DEMO_WINDOW_DAYS = 90;
const AM_TP_DEMO_MAX_FILINGS = 10;

export const useUpcomingFilings = () => {
  const { companyId } = useConfig();

  // This card is a live client surface, so the production lens has to stay reachable: with the flag off every
  // value below reduces to the expression it replaced, down to the rendered empty-state string.
  const isAmTpDemoOn = isFeatureLaunched(FeatureLaunch.AM_TP_DEMO);
  const windowDays = isAmTpDemoOn ? AM_TP_DEMO_WINDOW_DAYS : WINDOW_DAYS;
  const maxFilings = isAmTpDemoOn ? AM_TP_DEMO_MAX_FILINGS : MAX_FILINGS;

  const startDate = useMemo(
    () => NonTimezoneSensitiveDate.now().toISO8601String(),
    []
  );

  const endDate = useMemo(
    () =>
      NonTimezoneSensitiveDate.fromISO8601String(startDate)
        .add(windowDays, 'days')
        .toISO8601String(),
    [startDate, windowDays]
  );

  const { dates, isLoading, calendarNotGenerated } =
    useComplianceCalendarEvents({
      companyId: companyId ?? 0,
      startDate,
      endDate,
      enabled: !!companyId,
    });

  const eventsByDate = useMemo(() => {
    const soonest = adaptEvents(dates)
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .slice(0, maxFilings);

    return groupEventsByDate(soonest);
  }, [dates, maxFilings]);

  return {
    eventsByDate,
    isLoading,
    calendarNotGenerated,
    today: NonTimezoneSensitiveDate.fromISO8601String(startDate),
    // Named for the window rather than for seven days, because it is no longer always seven. The agenda clips
    // events to this date, so it has to move with the fetch range or the card claims filings it never renders.
    windowEnd: NonTimezoneSensitiveDate.fromISO8601String(endDate),
    windowDays,
  };
};
